// Rilevazione affidabile della modalità di avvio di Orbit (browser vs app installata standalone).
// Non modifica l'UI: espone helper riutilizzabili + attributi su <html> per la futura "Orbit Desktop".

export function isStandalone() {
  if (typeof window === "undefined") return false;
  const mm = (q) => window.matchMedia && window.matchMedia(q).matches;
  return (
    mm("(display-mode: standalone)") ||
    mm("(display-mode: minimal-ui)") ||
    mm("(display-mode: fullscreen)") ||
    mm("(display-mode: window-controls-overlay)") ||
    window.navigator.standalone === true // iOS/iPadOS Safari
  );
}

// Piattaforma effettiva per il flusso "Installa l'app": "ios" | "android" | "desktop".
// userAgentData esiste solo su Chromium (mai su iOS, dove ogni browser è WebKit): se presente non è iOS.
// iPadOS si presenta come "Macintosh": lo distinguiamo dal Mac reale solo con touch + assenza di userAgentData.
export function detectInstallPlatform() {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  const uad = navigator.userAgentData;
  const uadPlatform = (uad && uad.platform) || "";
  if (/android/i.test(uadPlatform) || /Android/i.test(ua)) return "android";
  if (uad) return uad.mobile ? "android" : "desktop";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  const macLike = /Macintosh/i.test(ua) || navigator.platform === "MacIntel";
  if (macLike && (navigator.maxTouchPoints || 0) > 1 && typeof document !== "undefined" && "ontouchend" in document) {
    return "ios";
  }
  return "desktop";
}

// Desktop "vero": puntatore fine (mouse) e nessun input touch primario.
export function isDesktop() {
  if (typeof window === "undefined") return false;
  const coarse = window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
  const touch = (navigator.maxTouchPoints || 0) > 0;
  return !coarse && !touch;
}

// Orbit avviata come app installata su desktop (useremo questo per abilitare "Orbit Desktop").
// NOTA: rilevazione REALE, non alterata da alcun override di sviluppo.
export function isDesktopStandalone() {
  return isStandalone() && isDesktop();
}

// --- Override grafico di SVILUPPO (SOLO Preview/localhost) -------------------
// Consente di simulare la UI "Orbit Desktop" in Preview senza installare la PWA.
// Non tocca isStandalone()/isDesktop()/isDesktopStandalone(): agisce solo come
// sorgente aggiuntiva per l'esperienza desktop e viene ignorato in produzione.
const PREVIEW_DESKTOP_KEY = "orbit-preview-desktop";
export const PREVIEW_DESKTOP_EVENT = "orbit-preview-desktop-change";

// Ambiente Preview/sviluppo Emergent (mai true in produzione/dominio reale).
export function isPreviewEnv() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname || "";
  return (
    h.endsWith(".preview.emergentagent.com") ||
    h === "localhost" ||
    h === "127.0.0.1"
  );
}

export function getPreviewDesktopOverride() {
  if (!isPreviewEnv()) return false; // in PROD l'override non esiste
  try {
    return window.localStorage.getItem(PREVIEW_DESKTOP_KEY) === "1";
  } catch (_) {
    return false;
  }
}

export function setPreviewDesktopOverride(on) {
  if (!isPreviewEnv()) return; // no-op fuori dalla Preview
  try {
    if (on) window.localStorage.setItem(PREVIEW_DESKTOP_KEY, "1");
    else window.localStorage.removeItem(PREVIEW_DESKTOP_KEY);
  } catch (_) {
    /* storage non disponibile */
  }
  markDisplayMode();
  try {
    window.dispatchEvent(new Event(PREVIEW_DESKTOP_EVENT));
  } catch (_) {}
}

// Esperienza desktop "effettiva" usata dalla UI: app installata REALE
// OPPURE override di sviluppo (solo Preview). Le funzioni di rilevamento reale restano intatte.
export function isDesktopExperience() {
  return isDesktopStandalone() || getPreviewDesktopOverride();
}

// Applica su <html> attributi/classe coerenti; si aggiorna anche se l'utente installa ad app aperta.
export function markDisplayMode() {
  if (typeof document === "undefined") return;
  const apply = () => {
    const el = document.documentElement;
    const standalone = isStandalone();
    const desktopExp = isDesktopExperience();
    el.dataset.orbitDisplay = standalone ? "standalone" : "browser";
    el.dataset.orbitPlatform = isDesktop() || desktopExp ? "desktop" : "mobile";
    el.classList.toggle("orbit-standalone", standalone);
    // Stesso hook della vera app desktop; in Preview può essere attivato dall'override.
    el.classList.toggle("orbit-desktop-app", desktopExp);
  };
  apply();
  try {
    window.matchMedia("(display-mode: standalone)").addEventListener("change", apply);
  } catch (_) {
    /* Safari legacy: nessun listener, l'attributo resta quello iniziale */
  }
  window.addEventListener("appinstalled", apply);
}
